import {
  TableRow,
  TableCell,
  Table,
  TableHeader,
  TableHeading,
  TableBody,
  Button,
} from '@insurancemenu/imenu-components-lib';
import { normDateToShortAmericanDate } from '@insurancemenu/imenu-components-lib/dist/helpers/dateFormatters';
import { Duplicate } from '@insurancemenu/imenu-components-lib/dist/icons';
import ActionModal from 'components/common/modals/ActionModal';
import { copyTextToBuffer } from 'global/utils/copyTextToBuffer';
import { ModalType } from 'global/utils/ModalsHelper';
import React from 'react';
import { useRootStore } from 'store/rootStore';
import { EnrollmentPeriodTypeTitle, GenerationStepTitle } from 'store/ui-stores/files-tracking/policyDashboardStore';
import { Policy } from 'types/policyService';
import { createStyles } from './styles';

export interface PolicyInfoModalProps {
  policy: Policy | null;
  isModalOpen: boolean;
}
const PolicyInfoModal = ({ policy, isModalOpen }: PolicyInfoModalProps): JSX.Element => {
  const classes = createStyles();
  if (!policy) return <></>;

  const {
    id,
    sequentialGroupId,
    group,
    agency,
    creationDate,
    generationStep,
    enrollmentPeriod,
    sequentialProductLineIds,
  } = policy;

  const {
    policyDashboardStore: { closeModal },
  } = useRootStore();

  const SequentialProductLineIds = sequentialProductLineIds
    ? Object.keys(sequentialProductLineIds).map((pLineId) => (
        <span key={pLineId} className={classes.pLineId}>{`${pLineId}: ${sequentialProductLineIds[pLineId]}`}</span>
      ))
    : [];

  const policyInfo = {
    'Policy Id': id,
    'Sponsor Id': policy?.enrollmentPeriod.domainId,
    'IM Group Id': sequentialGroupId,
    'Carrier Group Number': group.carrierGroupNumber,
    'IM Group UUID': group.id,
    'Group Name': group.name,
    'Enrollment Type': EnrollmentPeriodTypeTitle[enrollmentPeriod.type],
    'Agency Name': agency.name,
    'Generation Step': GenerationStepTitle[generationStep],
    'Effective Date': policy?.enrollmentPeriod.effectiveDate
      ? normDateToShortAmericanDate(policy?.enrollmentPeriod.effectiveDate)
      : '',
    'Creation Date': creationDate ? normDateToShortAmericanDate(creationDate) : '',
    'Sequential Product Line Ids': <>{SequentialProductLineIds}</>,
  };

  const copyPolicyIds = (): void => {
    copyTextToBuffer(policyInfo['Policy Id']);
  };

  // eslint-disable-next-line react/no-unstable-nested-components
  const PolicyInfoModalContent = (): JSX.Element => {
    const rowsList = Object.entries(policyInfo).map(([key, value]) => (
      <TableRow key={key} className={classes.tableRow}>
        <TableCell className={classes.tableCell}>{key}</TableCell>
        <TableCell className={classes.tableCell}>
          {key === 'Policy Id' ? (
            <div className={classes.policyIdCellContent}>
              <span>{value}</span>
              <Button type="icon" color="primary" onClick={copyPolicyIds} iconSize="3rem">
                <Duplicate width="2.4rem" height="2.4rem" />
              </Button>
            </div>
          ) : (
            value
          )}
        </TableCell>
      </TableRow>
    ));

    return (
      <div className={classes.policyInfoTableWrapper}>
        <Table totalElements={Object.keys(policyInfo).length}>
          <TableHeader className={classes.tableHeader}>
            <TableRow>
              <TableHeading className={classes.tableHeading}>Parameter</TableHeading>
              <TableHeading className={classes.tableHeading}>Value</TableHeading>
            </TableRow>
          </TableHeader>
          <TableBody className={classes.tableBody}>{rowsList}</TableBody>
        </Table>
      </div>
    );
  };
  return (
    <ActionModal
      show={isModalOpen}
      title="Full Policy Information"
      subtitle="Essential Information on the Policy Provided for Reference"
      modalType={ModalType.NO_ICON}
      closeButtonText="Close"
      onClose={closeModal}
      modalContent={PolicyInfoModalContent()}
    />
  );
};

export default PolicyInfoModal;
